import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/lib/utils";

export interface MultiSearchableSelectOption {
  value: string;
  label: string;
}

export interface MultiSearchableSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: MultiSearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  viewAllLabel?: string;
  className?: string;
  triggerClassName?: string;
}

export function MultiSearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select",
  searchPlaceholder = "Search",
  viewAllLabel = "View All...",
  className,
  triggerClassName,
}: MultiSearchableSelectProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const resolvedPlaceholder = placeholder ?? t("actions.select");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("actions.search");
  const resolvedViewAllLabel = viewAllLabel ?? t("actions.viewAll");

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, search]);

  const selectedLabels = useMemo(() => {
    const set = new Set(value);
    return options.filter((o) => set.has(o.value)).map((o) => o.label);
  }, [options, value]);

  const displayText =
    selectedLabels.length === 0
      ? resolvedPlaceholder
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-[#E0E0E0] bg-white px-3 py-2 text-sm text-[#333333] hover:bg-[#FAFAFA]",
            triggerClassName,
          )}
        >
          <span className={cn(value.length === 0 && "text-[#9CA3AF]")}>
            {displayText}
          </span>
          <span className="text-[#9CA3AF]">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-[var(--radix-popover-trigger-width)] p-0 bg-white",
          className,
        )}
        align="start"
      >
        <div className="p-2 border-b border-[#E5E7EB]">
          <div className="relative">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={resolvedSearchPlaceholder}
              className="h-8 pl-2 pr-8 text-sm"
            />
            <Search className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
          </div>
        </div>

        <div className="max-h-[220px] overflow-y-auto p-2">
          {filtered.map((opt) => {
            const checked = value.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex items-center gap-2 py-1.5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...value, opt.value]
                      : value.filter((v) => v !== opt.value);
                    onValueChange(Array.from(new Set(next)));
                  }}
                  className="h-4 w-4 rounded border-[#D1D5DB] accent-[#3B82F6]"
                />
                <span className="text-sm text-[#333333]">{opt.label}</span>
              </label>
            );
          })}
          {filtered.length === 0 && (
            <p className="px-2 py-2 text-sm text-[#6B7280]">{t("filter.noResults")}</p>
          )}
        </div>

        <div className="border-t border-[#E5E7EB] p-2">
          <button
            type="button"
            className="text-xs text-[#3B82F6] hover:underline"
            onClick={() => setSearch("")}
          >
            {resolvedViewAllLabel}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
